import React, { useEffect, useMemo, useRef, useState } from "react";
import { Anchor, AlertTriangle, Printer, Loader2, FileText, Eye, Calendar } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { BASELINE_SCHEMA } from "../lib/baselineSchema";

/**
 * Public share page — opens at `/share/:token`.
 *
 * No auth required. Verifies the token via `GET /api/share/{token}`, then
 * fetches the full sailor data and parameter ranges and renders the medical
 * report inline (full, issues, or latest snapshot depending on `report_type`).
 *
 * Designed mobile-first: large readable text, generous spacing, "Save as PDF"
 * uses the browser's native print dialog (works on iOS/Android/desktop).
 *
 * Security note: anyone with the token can view this page until the link
 * expires (7 days) or is revoked. Token is 128-bit entropy so unguessable.
 */
export default function PublicSharePage({ token }) {
  const [status, setStatus] = useState("loading"); // loading | error | ok
  const [errorMsg, setErrorMsg] = useState("");
  const [meta, setMeta] = useState(null);    // { sailor_id, sailor_name, report_type, expires_at, ... }
  const [detail, setDetail] = useState(null);
  const [parameters, setParameters] = useState([]);
  const reportRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const m = await api.get(`/share/${token}`);
        setMeta(m.data);
        // The share endpoint returns the sailor + parameter metadata in the
        // same payload — no follow-up calls needed (and none would work
        // anyway since the recipient has no auth token).
        setDetail(m.data.sailor || null);
        setParameters(m.data.parameters || []);
        setStatus("ok");
      } catch (e) {
        const code = e?.response?.status;
        setErrorMsg(
          e?.response?.data?.detail
          || (code === 404 ? "This share link is invalid." : "Couldn't load this report — please ask the coach for a new link.")
        );
        setStatus("error");
      }
    })();
  }, [token]);

  if (status === "loading") {
    return (
      <Frame>
        <div className="text-slate-400 flex items-center gap-2" data-testid="share-loading">
          <Loader2 className="animate-spin" size={18} /> Loading report…
        </div>
      </Frame>
    );
  }

  if (status === "error") {
    return (
      <Frame>
        <div className="border border-rose-500/40 bg-rose-500/5 rounded-lg p-5 text-rose-100" data-testid="share-error">
          <div className="flex items-center gap-2 font-display text-lg">
            <AlertTriangle size={18} className="text-rose-300" /> Link not available
          </div>
          <div className="mt-2 text-sm text-rose-200/80">{errorMsg}</div>
          <div className="mt-4 text-xs text-slate-500">
            Share links expire 7 days after creation. Contact the coaching team for an up-to-date copy.
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <Header meta={meta} sailorName={detail?.name} reportRef={reportRef} />
      <div ref={reportRef} className="public-share-printable">
        <ReportBody detail={detail} parameters={parameters} mode={meta.report_type} />
      </div>
      <Footer meta={meta} />
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#070D1A] text-slate-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </div>
    </div>
  );
}

function Header({ meta, sailorName, reportRef }) {
  const handlePrint = () => {
    // Use the browser native print dialog. iOS and Android both let the
    // user pick "Save as PDF" or "Send to printer" from this dialog.
    window.print();
  };
  const reportLabel =
    meta.report_type === "issues" ? "Issues only"
    : meta.report_type === "latest" ? "Latest snapshot"
    : "Full medical history";
  return (
    <div className="mb-5 print:hidden" data-noprint-toolbar>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-cyan-300">
          <Anchor size={16} />
          <span className="text-xs uppercase tracking-widest font-mono-data">YCH Sailing Health</span>
        </div>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/25"
          data-testid="share-download-pdf"
        >
          <Printer size={12} /> Save as PDF
        </button>
      </div>
      <div className="mt-4 border-l-2 border-cyan-500/40 pl-3 flex items-center gap-4">
        <img
          src="/branding/favicon-192.png"
          alt="YCH"
          className="w-14 h-14 rounded-lg bg-white p-1 object-contain border border-cyan-500/40 shrink-0 print:bg-white"
        />
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl sm:text-3xl text-slate-50">{sailorName || meta.sailor_name}</div>
          <div className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1"><FileText size={11} />{reportLabel}</span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} /> Link expires {fmtDate(meta.expires_at?.slice(0, 10))}
            </span>
            <span className="inline-flex items-center gap-1"><Eye size={11} /> {meta.view_count} view{meta.view_count === 1 ? "" : "s"}</span>
          </div>
          {meta.note && (
            <div className="mt-3 text-sm text-slate-300 bg-slate-800/40 border border-slate-700 rounded-md px-3 py-2 italic">
              &ldquo;{meta.note}&rdquo;
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Footer({ meta }) {
  return (
    <div className="mt-10 text-center text-[10px] text-slate-600 print:hidden" data-noprint-toolbar>
      Shared with {meta.recipient_name || meta.recipient_type}. This is a read-only copy from YCH Sailing Health.
      Contact the coaching team for any clarifications.
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportBody — slimmed-down version of `MedicalHistoryReport` purpose-built
// for the share view. We deliberately don't reuse MedicalHistoryReport itself
// because that component renders inside a modal + portal with a full
// dashboard wrapper around it. Here we want a clean, mobile-first page.
// ---------------------------------------------------------------------------
function ReportBody({ detail, parameters, mode }) {
  const paramByKey = useMemo(() => {
    return Object.fromEntries((parameters || []).map((p) => [p.key, p]));
  }, [parameters]);

  const records = useMemo(() => {
    return (detail?.records || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [detail]);

  const latestLab = useMemo(
    () => records.find((r) => (r.type || "lab") === "lab" && r.params && Object.keys(r.params).length > 0),
    [records],
  );

  // ISSUES MODE — only out-of-range markers from latest lab.
  if (mode === "issues") {
    const issues = [];
    if (latestLab?.status) {
      for (const p of parameters) {
        const st = latestLab.status[p.key];
        if (st === "low" || st === "high") {
          issues.push({ ...p, value: latestLab.params[p.key], status: st });
        }
      }
    }
    return (
      <div>
        <SectionTitle>Latest lab — {fmtDate(latestLab?.date) || "no data"}</SectionTitle>
        {issues.length === 0 ? (
          <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-5 text-emerald-200">
            All parameters within range.
          </div>
        ) : (
          <div className="space-y-2">
            {issues.map((iss) => <IssueLine key={iss.key} iss={iss} />)}
          </div>
        )}
        <Disclaimer />
      </div>
    );
  }

  // LATEST MODE — latest lab values (all) + baseline summary.
  if (mode === "latest") {
    return (
      <div>
        <SectionTitle>Latest lab snapshot — {fmtDate(latestLab?.date) || "no data"}</SectionTitle>
        {latestLab && (
          <ParamGrid record={latestLab} parameters={parameters} />
        )}
        <SectionTitle className="mt-6">Baseline summary</SectionTitle>
        <BaselineSummary baseline={detail?.baseline} />
        <Disclaimer />
      </div>
    );
  }

  // FULL MODE — every record chronologically + baseline.
  return (
    <div>
      <SectionTitle>Baseline assessment</SectionTitle>
      <BaselineSummary baseline={detail?.baseline} />
      <SectionTitle className="mt-6">Records ({records.length})</SectionTitle>
      <div className="space-y-3">
        {records.map((r) => (
          <RecordCard key={r.id} record={r} parameters={parameters} paramByKey={paramByKey} />
        ))}
      </div>
      <Disclaimer />
    </div>
  );
}

function SectionTitle({ children, className = "" }) {
  return (
    <div className={`text-cyan-300 uppercase tracking-widest text-[11px] font-mono-data border-b border-cyan-500/20 pb-1.5 mb-3 ${className}`}>
      {children}
    </div>
  );
}

function IssueLine({ iss }) {
  const colour = iss.status === "high" ? "text-rose-300 border-rose-500/40 bg-rose-500/5" : "text-amber-300 border-amber-500/40 bg-amber-500/5";
  return (
    <div className={`border-l-2 pl-3 py-1.5 ${colour}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-display text-slate-100">{iss.label}</span>
        <span className="font-mono-data text-sm">
          {iss.value} {iss.unit} · <span className="uppercase tracking-wider">{iss.status}</span>
        </span>
      </div>
      <div className="text-[10px] text-slate-400 mt-0.5 font-mono-data">
        Normal: {iss.range_low} – {iss.range_high} {iss.unit}
      </div>
    </div>
  );
}

function ParamGrid({ record, parameters }) {
  const filled = (parameters || []).filter((p) => record.params?.[p.key] != null);
  if (filled.length === 0) {
    return <div className="text-slate-500 text-sm italic">No values recorded.</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {filled.map((p) => {
        const v = record.params[p.key];
        const st = record.status?.[p.key];
        const ringClass =
          st === "high" ? "border-rose-500/40 text-rose-200" :
          st === "low"  ? "border-amber-500/40 text-amber-200" :
                          "border-slate-700 text-slate-200";
        return (
          <div key={p.key} className={`border ${ringClass} bg-slate-900/40 rounded-md px-2.5 py-1.5`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-data truncate">
              {p.label}
            </div>
            <div className="font-mono-data text-sm">
              {v} <span className="text-[10px] text-slate-500">{p.unit}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Map record type to display tag. Used in the public share page header
// strip per record card.
function recordTag(type) {
  if (!type || type === "lab") return "LAB";
  if (type === "consult") return "CONSULT";
  if (type === "biometric") return "BIOMETRIC";
  return type.toUpperCase();
}

function RecordCard({ record, parameters, paramByKey }) {
  const isLab = (record.type || "lab") === "lab";
  const tag = recordTag(record.type);
  return (
    <div className="border border-slate-800 bg-slate-900/30 rounded-md p-3">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="font-mono-data text-sm text-slate-200">{fmtDate(record.date)}</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-data">{tag}</span>
      </div>
      {record.dr && <div className="text-sm text-slate-300">Dr {record.dr}</div>}
      {record.hospital && <div className="text-xs text-slate-500">{record.hospital}</div>}
      {record.advice && (
        <div className="mt-1.5 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-800/40 rounded px-2 py-1.5">
          {record.advice}
        </div>
      )}
      {isLab && record.params && Object.keys(record.params).length > 0 && (
        <div className="mt-2">
          <ParamGrid record={record} parameters={parameters} />
        </div>
      )}
    </div>
  );
}

function BaselineSummary({ baseline }) {
  if (!baseline || Object.keys(baseline).length === 0) {
    return <div className="text-slate-500 text-sm italic">No baseline assessment on file.</div>;
  }
  // Show every populated field from every section, in schema order.
  const rows = [];
  for (const section of BASELINE_SCHEMA) {
    for (const f of section.fields) {
      const v = baseline[f.key];
      if (v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      rows.push({ label: f.label, value: Array.isArray(v) ? v.join(", ") : String(v) });
    }
  }
  if (rows.length === 0) {
    return <div className="text-slate-500 text-sm italic">Baseline form is empty.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[13px]">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline gap-2 border-b border-slate-800/60 pb-1">
          <span className="text-slate-500 text-[11px] uppercase tracking-wider font-mono-data shrink-0 w-32 truncate">{r.label}</span>
          <span className="text-slate-200 break-words">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="mt-8 text-[10px] text-slate-500 border-t border-slate-800 pt-3 leading-relaxed">
      Confidential medical information shared by the YCH Sailing coaching team via a one-time link.
      For medical advice please consult a qualified physician.
    </div>
  );
}
